import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Grid,
  useTheme,
  alpha,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  WorkOutline,
  LocationOn,
  CalendarToday,
  People,
  KeyboardArrowRight,
  Edit,
  Delete,
  Public,
  Computer,
  FeaturedPlayList,
  MoreVert,
  Visibility,
  Archive,
  Pause,
  PlayArrow,
  AllInclusive,
  Close,
} from '@mui/icons-material';
import { Project, ProjectStatus, projectStatusColors } from '../../types/Project';

interface ProjectListItemProps {
  project: Project;
  onView: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onUpdateStatus?: (id: string, status: ProjectStatus) => void;
}

const ProjectListItem: React.FC<ProjectListItemProps> = ({ 
  project, 
  onView, 
  onEdit,
  onDelete,
  onUpdateStatus
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const formatExperience = () => {
    if (!project.experience) return 'N/A';
    
    if (project.experience.min === project.experience.max) {
      return `${project.experience.min} years`;
    }
    
    return `${project.experience.min}-${project.experience.max} years`;
  };

  const formatWorkModeIcon = () => {
    switch (project.work_mode) {
      case 'remote':
        return <Computer fontSize="small" color="action" />;
      case 'hybrid':
        return <Public fontSize="small" color="action" />;
      default:
        return <LocationOn fontSize="small" color="action" />;
    }
  };

  const formatJobType = (jobType: string) => {
    return jobType.replace('_', ' ');
  };

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: 'none',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        '&:hover': {
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease',
        },
      }}
    >
      <Box
        sx={{
          height: 6,
          width: '100%',
          backgroundColor: projectStatusColors[project.status],
        }}
      />
      <CardContent sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Title and basic info */}
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {project.project_name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              <Chip
                label={project.status}
                size="small"
                sx={{
                  textTransform: 'capitalize',
                  bgcolor: alpha(projectStatusColors[project.status], 0.1),
                  color: projectStatusColors[project.status],
                  fontWeight: 500,
                  borderRadius: 1,
                }}
              />
              <Chip
                label={formatJobType(project.job_type)}
                size="small"
                variant="outlined"
                sx={{ 
                  borderRadius: 1,
                  textTransform: 'capitalize',
                }}
              />
              <Chip
                label={project.work_mode.replace('_', ' ')}
                size="small"
                variant="outlined"
                sx={{ 
                  borderRadius: 1,
                  textTransform: 'capitalize',
                }}
              />
            </Box>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {project.description || 'No description provided'}
            </Typography>
          </Grid>

          {/* Details */}
          <Grid item xs={12} md={4}>
            <Box>
              {(project.start_date || project.end_date || project.application_deadline) && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarToday fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {project.start_date && new Date(project.start_date).toLocaleDateString()}
                    {project.end_date && ` - ${new Date(project.end_date).toLocaleDateString()}`}
                    {project.application_deadline && ` (Apply by: ${new Date(project.application_deadline).toLocaleDateString()})`}
                  </Typography>
                </Box>
              )}
              
              {project.location && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {formatWorkModeIcon()}
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {project.work_mode === 'remote' ? 'Remote' : project.location}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WorkOutline fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2">{formatExperience()}</Typography>
              </Box>
              
              {project.salary && project.salary.is_displayed && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {project.salary.min && project.salary.max 
                      ? `${project.salary.currency} ${project.salary.min.toLocaleString()}-${project.salary.max.toLocaleString()}`
                      : project.salary.min 
                        ? `${project.salary.currency} ${project.salary.min.toLocaleString()}+` 
                        : 'Competitive'
                    } / {project.salary.period}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Skills and actions */}
          <Grid item xs={12} md={3}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                  Required Skills:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {project.must_have_skills.slice(0, 3).map((skill) => (
                    <Chip
                      key={skill}
                      label={skill}
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        height: 20,
                        borderRadius: 1,
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      }}
                    />
                  ))}
                  {project.must_have_skills.length > 3 && (
                    <Chip
                      label={`+${project.must_have_skills.length - 3}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '0.7rem',
                        height: 20,
                        borderRadius: 1,
                      }}
                    />
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Box>
                  {onEdit && onDelete && onUpdateStatus && (
                    <>
                      <Tooltip title="More Actions">
                        <IconButton 
                          onClick={handleMenuClick}
                          size="small" 
                          color="default"
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                      >
                        <MenuItem 
                          onClick={() => {
                            onEdit(project._id);
                            handleMenuClose();
                          }}
                        >
                          <ListItemIcon>
                            <Edit fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Edit Project</ListItemText>
                        </MenuItem>
                        
                        {/* Status update options */}
                        {project.status !== 'active' && (
                          <MenuItem 
                            onClick={() => {
                              onUpdateStatus(project._id, 'active');
                              handleMenuClose();
                            }}
                          >
                            <ListItemIcon>
                              <PlayArrow fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText>Activate</ListItemText>
                          </MenuItem>
                        )}
                        
                        {project.status !== 'paused' && project.status !== 'closed' && project.status !== 'archived' && (
                          <MenuItem 
                            onClick={() => {
                              onUpdateStatus(project._id, 'paused');
                              handleMenuClose();
                            }}
                          >
                            <ListItemIcon>
                              <Pause fontSize="small" color="warning" />
                            </ListItemIcon>
                            <ListItemText>Pause</ListItemText>
                          </MenuItem>
                        )}
                        
                        {project.status !== 'closed' && project.status !== 'archived' && (
                          <MenuItem 
                            onClick={() => {
                              onUpdateStatus(project._id, 'closed');
                              handleMenuClose();
                            }}
                          >
                            <ListItemIcon>
                              <Close fontSize="small" color="error" />
                            </ListItemIcon>
                            <ListItemText>Close</ListItemText>
                          </MenuItem>
                        )}
                        
                        {project.status !== 'archived' && (
                          <MenuItem 
                            onClick={() => {
                              onUpdateStatus(project._id, 'archived');
                              handleMenuClose();
                            }}
                          >
                            <ListItemIcon>
                              <Archive fontSize="small" color="secondary" />
                            </ListItemIcon>
                            <ListItemText>Archive</ListItemText>
                          </MenuItem>
                        )}
                        
                        <MenuItem
                          onClick={() => {
                            onDelete(project._id);
                            handleMenuClose();
                          }}
                          sx={{ color: theme.palette.error.main }}
                        >
                          <ListItemIcon>
                            <Delete fontSize="small" color="error" />
                          </ListItemIcon>
                          <ListItemText>Delete</ListItemText>
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<KeyboardArrowRight />}
                  onClick={() => onView(project._id)}
                  sx={{ ml: 'auto' }}
                >
                  View Details
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ProjectListItem;